import { useEffect, useRef } from 'react';
import type { DealValues } from '../../api/registryDealTypes';

const SAVE_DELAY_MS = 700;

/**
 * Collects field changes and saves them together once typing pauses. Saves run one after
 * another, so a slow save never overwrites a later one. `save` gets the batch being saved and
 * whatever was changed while it ran.
 */
export function useSetupAutosave(
  save: (
    patch: DealValues,
    completedStep: string | undefined,
    stillPending: DealValues,
  ) => Promise<void>,
  onError: (failure: unknown) => void,
) {
  const pending = useRef<DealValues>({});
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const queue = useRef<Promise<unknown>>(Promise.resolve());

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const flush = async (completedStep?: string) => {
    if (timer.current) clearTimeout(timer.current);
    const patch = pending.current;
    pending.current = {};
    if (Object.keys(patch).length === 0 && !completedStep) return;
    queue.current = queue.current
      .catch(() => undefined)
      .then(() => save(patch, completedStep, pending.current));
    await queue.current;
  };

  const queueChange = (key: string, value: DealValues[string]) => {
    pending.current[key] = value;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      flush().catch(onError);
    }, SAVE_DELAY_MS);
  };

  return { flush, queueChange };
}
